import { useState, useEffect, lazy, Suspense } from "react";
import { Calendar, MapPin, Plane, Plus, Wallet } from "lucide-react";
import type { DashboardData, Owner, Transaction, Trip } from "../../types";
import { dateLabel, money } from "../../lib/finance";
import SecondaryHeading from "../../app/SecondaryHeading";
import Empty from "../../components/Empty";
import { Private, usePrivacy } from "../../components/Privacy";
import TripDetail from "./TripDetail";
import { useTripStats } from "../../hooks/use-trip-stats";

const TripForm = lazy(() => import("./TripForm"));

export interface TripsProps {
  data: DashboardData;
  profile: Owner;
  onSaved: () => Promise<unknown> | void;
  onOpenTransaction: (t: Transaction) => void;
  onAddTransaction?: (prefill: {
    tripId?: string;
    tripDestination?: string;
    type?: "Expense" | "Income" | "Transfer";
    accountId?: string;
    toAccountId?: string;
    amount?: string;
    exchangeRate?: string;
    notes?: string;
  }) => void;
  focusTripId?: string | null;
  onFocusHandled?: () => void;
}

function TripCard({
  trip,
  data,
  onClick,
}: {
  trip: Trip;
  data: DashboardData;
  onClick: () => void;
}) {
  const { hidden } = usePrivacy();
  const stats = useTripStats(trip, data);
  const totalSpend = stats?.totalSpendBudget ?? 0;
  const budgetCurrency = stats?.budgetCurrency ?? data.baseCurrency;
  const budget = trip.budget;
  const budgetPercent = stats?.budgetPercent ?? 0;
  const isOver = stats?.isOverBudget ?? false;
  const remaining = stats?.remainingBudget ?? null;

  return (
    <button type="button" className="trip-card" onClick={onClick}>
      <div className="trip-card-top">
        <span className="account-type-icon trip-card-icon">
          <Plane size={18} />
        </span>
        <div className="trip-card-header-text">
          <h3>{trip.name}</h3>
          <span className="trip-card-dates">
            <Calendar size={12} />
            {dateLabel(trip.startDate)} – {dateLabel(trip.endDate)}
          </span>
        </div>
        <span className={`trip-status-badge ${trip.status}`}>{trip.status}</span>
      </div>

      {(trip.destinations || []).length > 0 && (
        <div className="trip-card-destinations">
          {(trip.destinations || []).map((d) => (
            <span key={d} className="destination-tag">
              <MapPin size={11} /> {d}
            </span>
          ))}
        </div>
      )}

      <div className="trip-card-spending">
        <div className="trip-card-figures">
          <div>
            <span className="trip-card-figure-label">Spent</span>
            <div className="trip-card-figure-val">
              <Private>
                <strong>{money(totalSpend, budgetCurrency).replace(` ${budgetCurrency}`, "")}</strong>
              </Private>
              <span>{budgetCurrency}</span>
            </div>
          </div>
          {budget !== null && (
            <div className="trip-card-budget-side">
              <span className="trip-card-figure-label">Budget: <Private>{money(budget, budgetCurrency)}</Private></span>
              <span className={`trip-card-budget-status ${isOver ? "over" : "under"}`}>
                {remaining !== null && (
                  isOver
                    ? <><Private>{money(Math.abs(remaining), budgetCurrency)}</Private> over</>
                    : <><Private>{money(remaining, budgetCurrency)}</Private> left</>
                )}
              </span>
            </div>
          )}
        </div>

        {budget !== null && !hidden && (
          <div className={`budget-bar-track ${isOver ? "over" : ""}`}>
            <div
              className="budget-bar-fill"
              style={{ width: `${Math.min(100, budgetPercent)}%` }}
            />
          </div>
        )}
      </div>

      <div className="trip-card-foot">
        <span>
          {stats && stats.walletBalances.length > 0 ? (
            <>
              <Wallet size={12} />
              {stats.walletBalances.length} cash wallet{stats.walletBalances.length === 1 ? "" : "s"}
            </>
          ) : (
            <span>&nbsp;</span>
          )}
        </span>
        <span>
          {stats?.tripTransactions.length ?? 0} expense{stats?.tripTransactions.length === 1 ? "" : "s"}
        </span>
      </div>
    </button>
  );
}

export default function Trips({
  data,
  profile,
  onSaved,
  onOpenTransaction,
  onAddTransaction,
  focusTripId,
  onFocusHandled,
}: TripsProps) {
  const [selectedTripId, setSelectedTripId] = useState<string | null>(focusTripId || null);
  const [adding, setAdding] = useState(false);
  const [showPastTrips, setShowPastTrips] = useState(false);

  useEffect(() => {
    if (focusTripId) {
      setSelectedTripId(focusTripId);
      onFocusHandled?.();
    }
  }, [focusTripId, onFocusHandled]);

  const trips = data.trips || [];
  const selectedTrip = trips.find((t) => t.id === selectedTripId);

  // If a trip is selected, render TripDetail
  if (selectedTrip) {
    return (
      <TripDetail
        trip={selectedTrip}
        data={data}
        profile={profile}
        onBack={() => setSelectedTripId(null)}
        onSaved={onSaved}
        onOpenTransaction={onOpenTransaction}
        onAddTransaction={(prefill) => {
          if (onAddTransaction) {
            onAddTransaction(prefill);
          }
        }}
      />
    );
  }

  const activeOrUpcoming = trips.filter(
    (t) => t.status === "active" || t.status === "planned",
  );
  const pastTrips = trips.filter(
    (t) => t.status === "completed" || t.status === "archived",
  );

  return (
    <section className="trips-panel">
      {adding && (
        <Suspense fallback={null}>
          <TripForm
            data={data}
            profile={profile}
            onDone={async () => {
              setAdding(false);
              await onSaved();
            }}
            onCancel={() => setAdding(false)}
          />
        </Suspense>
      )}

      <SecondaryHeading
        title="Trips"
        icon={<Plane size={18} />}
      >
        <button
          type="button"
          className="primary small"
          onClick={() => setAdding(true)}
        >
          <Plus size={14} /> Add trip
        </button>
      </SecondaryHeading>

      {trips.length === 0 ? (
        <Empty
          title="No trips planned yet"
          description="Create a trip to manage travel budgets, traveler cash wallets, currency conversion, and vacation expenses."
        />
      ) : (
        <div className="trips-list-view">
          {activeOrUpcoming.length > 0 && (
            <div className="trips-group">
              <div className="section-heading">
                <h3>Active & Upcoming</h3>
                <span className="subtle-tag">{activeOrUpcoming.length}</span>
              </div>
              <div className="trips-grid">
                {activeOrUpcoming.map((trip) => (
                  <TripCard
                    key={trip.id}
                    trip={trip}
                    data={data}
                    onClick={() => setSelectedTripId(trip.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {pastTrips.length > 0 && (
            <div className="trips-group past-trips-group">
              <div className="section-heading past-trips-heading">
                <div className="past-trips-heading-left">
                  <h3>Past Trips</h3>
                  <span className="subtle-tag">{pastTrips.length}</span>
                </div>
                <button
                  type="button"
                  className="text-button small subtle trip-past-toggle"
                  onClick={() => setShowPastTrips((s) => !s)}
                >
                  {showPastTrips ? "Hide past trips" : `Show past trips (${pastTrips.length})`}
                </button>
              </div>
              {showPastTrips && (
                <div className="trips-grid">
                  {pastTrips.map((trip) => (
                    <TripCard
                      key={trip.id}
                      trip={trip}
                      data={data}
                      onClick={() => setSelectedTripId(trip.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
